import React, { useState, useRef, useEffect, useMemo, CSSProperties } from "react";
import styled from "styled-components";

interface DropdownProps {
  options: any[];
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  keys: string[];
  label?: string;
  onChange: Function;
  value?: string;
  selector: string;
  optionDefaultHeight?: number;
}

export default function InputDropdown({
  onChange,
  value,
  containerStyle,
  options,
  style,
  keys,
  label,
  optionDefaultHeight,
  selector,
}: DropdownProps) {
  const [filterText, setFilterText] = useState(value || "");
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const InputRef = useRef<HTMLInputElement>(null);

  // Sync internal filterText with external value prop (without triggering onChange)
  useEffect(() => {
    const newValue = value || "";
    if (filterText !== newValue) {
      setFilterText(newValue);
    }
  }, [value]); // Only depends on value

  // Filter options based on filterText (unchanged)
  const filteredOptions = useMemo(() => {
    if (!filterText.trim()) return options;
    const filtered: any[] = [];
    for (const key of keys) {
      const matches = options.filter(
        (option) =>
          option[key] &&
          option[key].toLowerCase().includes(filterText.toLowerCase())
      );
      for (const match of matches) {
        if (!filtered.includes(match)) filtered.push(match);
      }
    }
    return filtered;
  }, [options, keys, filterText]);

  // Click outside handler (unchanged)
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (InputRef.current && !InputRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Called when user types – only updates filter & shows dropdown, does NOT call onChange
  const handleFilterChange = (text: string) => {
    setFilterText(text);
    setDropdownVisible(true);
  };

  // ✅ Called when user selects an option – updates filter, closes dropdown, AND notifies parent
  const handleOptionSelect = (option: string) => {
    setFilterText(option);
    setDropdownVisible(false);
    onChange(option); // parent receives the selected value (tour_id)
  };

  const dropdownHeight = useMemo(() => {
    const numOptions = filteredOptions.length;
    const optionHeight = optionDefaultHeight || 60;
    const calculatedHeight = numOptions * optionHeight;
    const finalHeight = calculatedHeight > 200 ? 200 : calculatedHeight;
    return isDropdownVisible ? finalHeight : 0;
  }, [isDropdownVisible, filteredOptions, optionDefaultHeight]);

  const renderItem = (item: any, idx: number) => (
    <OptionItem key={idx} onClick={() => handleOptionSelect(item[selector])}>
      {keys.map((key, i) => (
        <React.Fragment key={i}>
          {item[key]}
          <br />
        </React.Fragment>
      ))}
    </OptionItem>
  );

  return (
    <Main style={containerStyle}>
      {label && <label>{label}</label>}
      <DropdownContainer ref={InputRef} style={style}>
        <DropdownInput
          placeholder="Search..."
          value={filterText}
          onChange={(e) => handleFilterChange(e.target.value)}
          onFocus={() => setDropdownVisible(true)}
        />
        {isDropdownVisible && (
          <DropdownList style={{ height: `${dropdownHeight}px` }}>
            {filteredOptions.map(renderItem)}
          </DropdownList>
        )}
      </DropdownContainer>
    </Main>
  );
}

// Styled components (unchanged)
const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DropdownContainer = styled.div`
  position: relative;
  flex-direction: column;
  align-items: flex-start;
`;

const DropdownInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  font-size: 16px;
  outline: none;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.gray};
  height: 60px;
  padding: 10px 20px;
  &::placeholder {
    color: ${(props) => props.theme.gray};
  }
  &:focus {
    border-radius: 8px 8px 0 0;
  }
`;

const DropdownList = styled.div`
  position: absolute;
  z-index: 10;
  top: 60px;
  width: 100%;
  max-height: 200px;
  border: 1px solid ${(props) => props.theme.gray};
  background-color: #fff;
  overflow-y: auto;
`;

const OptionItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;